import React from 'react';
import { TrendingUp, TrendingDown, MessageSquare, Target, Clock } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';

export default function ConversationAnalytics({ analytics, conversations }) {
  // Prepare chart data
  const volumeData = analytics?.map(a => ({
    date: format(new Date(a.date), 'dd/MM'),
    conversations: a?.total_conversations,
    messages: a?.total_messages
  })) || [];

  const accuracyData = analytics?.map(a => ({
    date: format(new Date(a.date), 'dd/MM'),
    confidence: parseFloat(a?.avg_confidence_score) * 100,
    flagged: a?.flagged_count
  })) || [];

  // Topic distribution
  const topicCounts = {};
  conversations?.forEach(conv => {
    if (conv?.conversation_topic) {
      topicCounts[conv.conversation_topic] = (topicCounts?.[conv?.conversation_topic] || 0) + 1;
    }
  });

  const topicData = Object.entries(topicCounts)?.map(([topic, count]) => ({
    name: topic,
    value: count
  }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 5);

  const COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Calculate summary stats
  const totalConversations = analytics?.reduce((sum, a) => sum + a?.total_conversations, 0) || 0;
  const totalMessages = analytics?.reduce((sum, a) => sum + a?.total_messages, 0) || 0;
  const avgConfidence = analytics?.length > 0
    ? (analytics?.reduce((sum, a) => sum + parseFloat(a?.avg_confidence_score), 0) / analytics?.length * 100)?.toFixed(1)
    : 0;
  const totalFlagged = analytics?.reduce((sum, a) => sum + a?.flagged_count, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <MessageSquare size={24} />
            <TrendingUp size={20} />
          </div>
          <p className="text-2xl font-bold">{totalConversations}</p>
          <p className="text-sm opacity-90">Conversations (30j)</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Target size={24} />
            <TrendingUp size={20} />
          </div>
          <p className="text-2xl font-bold">{avgConfidence}%</p>
          <p className="text-sm opacity-90">Confiance Moyenne</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <MessageSquare size={24} />
            <Clock size={20} />
          </div>
          <p className="text-2xl font-bold">{totalMessages}</p>
          <p className="text-sm opacity-90">Messages Totaux</p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingDown size={24} />
          </div>
          <p className="text-2xl font-bold">{totalFlagged}</p>
          <p className="text-sm opacity-90">Signalements</p>
        </div>
      </div>
      {/* Volume Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Volume de Conversations</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={volumeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="conversations" stroke="#16a34a" strokeWidth={2} name="Conversations" />
            <Line type="monotone" dataKey="messages" stroke="#3b82f6" strokeWidth={2} name="Messages" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Accuracy Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Qualité des Réponses</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={accuracyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="confidence" fill="#16a34a" name="Confiance (%)" />
            <Bar yAxisId="right" dataKey="flagged" fill="#ef4444" name="Signalés" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Topic Distribution */}
      {topicData?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Sujets de Conversation</h3>
          <div className="flex flex-col md:flex-row items-center">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={topicData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100)?.toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {topicData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="md:ml-6 mt-4 md:mt-0">
              {topicData?.map((topic, index) => (
                <div key={topic?.name} className="flex items-center mb-2">
                  <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}></div>
                  <span className="text-sm text-gray-700">{topic?.name}: <strong>{topic?.value}</strong></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}